import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginForm, RegisterForm } from '@/components/auth';
import { useAuth } from '@/hooks';
import type { AuthMode } from '@/components/layout/AuthLayout';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { isEmailVerificationError } from '@/components/layout/AuthContext';
import { VerificationPending } from '@/components/auth/VerificationPending';
import { useUiStore } from '@/store/uiStore';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  agreed: boolean;
}

/**
 * LoginPage Route Component
 * Manages authentication state and toggles between Login and Register forms.
 */
export function LoginPage() {
  const { isAuthenticated, isLoading, error, login, register } = useAuth();
  const openModal = useUiStore((state) => state.openModal);
  const [view, setView] = React.useState<AuthMode>('login');
  const [verificationEmail, setVerificationEmail] = React.useState<string | null>(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated) {
      const redirectTo = sessionStorage.getItem('redirectAfterLogin') || '/';
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (data: { email: string; password: string }) => {
    try {
      await login(data.email, data.password);
    } catch (err) {
      if (isEmailVerificationError(err)) {
        setVerificationEmail(err.email);
        setView('verify-pending');
      }
    }
  };

  const handleRegister = async (data: RegisterFormData) => {
    try {
      const result = await register({
        email: data.email,
        password: data.password,
        first_name: data.firstName,
        last_name: data.lastName,
        accepted_terms: data.agreed,
        role: 'fan',
      });

      if (result && 'requires_email_verification' in result) {
        setVerificationEmail(result.email);
        setView('verify-pending');
      }
    } catch (err) {
      if (isEmailVerificationError(err)) {
        setVerificationEmail(err.email);
        setView('verify-pending');
      }
    }
  };

  const handleForgot = () => {
    setView('forgot');
  };

  const handleBackToLogin = () => {
    setVerificationEmail(null);
    setView('login');
  };

  const handleSwitch = () => {
    if (view === 'forgot' || view === 'verify-pending') {
      setVerificationEmail(null);
      setView('login');
    } else {
      setView(view === 'login' ? 'register' : 'login');
    }
  };

  // Define constants based on state
  const authType = view;
  const formId = 'auth-form';
  const submitLabel = view === 'register' ? 'CREATE ACCOUNT' : 'SIGN IN';
  const dividerText = view === 'register' ? 'or register with email' : 'or sign in with email';

  return (
    <AuthLayout
      authType={authType}
      onSwitch={handleSwitch}
      isLoading={isLoading}
      submitLabel={submitLabel}
      dividerText={dividerText}
      formId={formId}
    >
      {view === 'verify-pending' && verificationEmail ? (
        <VerificationPending email={verificationEmail} onBackToLogin={handleBackToLogin} />
      ) : view === 'register' ? (
        <RegisterForm
          formId={formId}
          onSubmit={handleRegister}
          error={error}
          isLoading={isLoading}
          onTermsClick={() => openModal('terms')}
        />
      ) : (
        <LoginForm
          formId={formId}
          onSubmit={handleLogin}
          onForgot={handleForgot}
          error={error}
          isLoading={isLoading}
        />
      )}
    </AuthLayout>
  );
}
